import React, { useEffect, useMemo, useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Layers, SearchX } from 'lucide-react';
import SchemaModal from '../../../ui/SchemaModal';
import Input from '../../../ui/Input';
import CardGrid from '../../../ui/CardGrid';
import CardImage from '../../../ui/CardImage';
import CardViewer from '../../../ui/CardViewer';
import EmptyState from '../../../ui/EmptyState';
import { useIsMobileViewport } from '../../../ui/breakpoints';
import { useCloseOnBack } from '../../../ui/backLayer/useCloseOnBack';
import { dragData, dragId } from '../hooks/usePlaytestDnd';
import { cardImageProps } from '../utils/cardView';
import './CardListViewer.css';

const cardName = (card) => card?.scryfallData?.name || '';

/**
 * Zonen-Viewer: eine Zone (Bibliothek, Friedhof, Exil) durchsehen.
 *
 *   <CardListViewer open={open} onOpenChange={setOpen} title="Friedhof" zone="graveyard"
 *     cards={player.graveyard} allowDrag onCardAction={(card, { x, y }) => …} />
 *
 * - `SchemaModal size="lg"`; das Feld oben filtert nach Kartenname (✕ und Escape leeren).
 * - Ohne Karten steht `emptyTitle` (Vorgabe „Keine Karten in dieser Zone").
 * - Raster: am Rechner `CardGrid variant="browse"` (lesbare Bildgröße), am Handy
 *   `variant="touch"` — dort öffnet ein Tipp die Karte im `CardViewer` (Blättern durch die
 *   gefilterte Liste), gelesen wird dort.
 * - `allowDrag`: jede Karte ist mit `dragData(card, zone)` ziehbar; `zone` ist die Herkunft, ohne
 *   sie löst ein Drop nichts aus. Das Modal bleibt beim Ziehen offen.
 * - Rechtsklick (am Handy der Langdruck des Browsers) → `onCardAction(card, { x, y })` in
 *   Client-Koordinaten; was sich öffnet, entscheidet die Seite.
 * - Der `CardViewer` liegt über dem Modal (`raised`) und hängt an der Zurück-Geste. Solange er
 *   offen ist, schließt ein Tipp außerhalb des Modals dieses nicht: der Viewer liegt per Portal
 *   außerhalb des Modal-Inhalts, jeder Tipp darauf wäre für Radix ein Klick daneben.
 * - Schließen setzt Suche und Viewer zurück.
 * - `children` rendert im Modal-Inhalt: Menüs oder Sheets, die aus dem Viewer heraus geöffnet
 *   werden, gehören in dessen React-Baum, sonst gilt jeder Klick darauf als Klick neben das Modal.
 */
export default function CardListViewer({
  open, onOpenChange, title, zone, cards = [], allowDrag = false, onCardAction, emptyTitle = 'Keine Karten in dieser Zone', children,
}) {
  const isMobile = useIsMobileViewport();
  const [query, setQuery] = useState('');
  const [viewerIndex, setViewerIndex] = useState(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setViewerIndex(null);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('de');
    if (!needle) return cards;
    return cards.filter((card) => cardName(card).toLocaleLowerCase('de').includes(needle));
  }, [cards, query]);

  const viewerOpen = viewerIndex !== null && filtered.length > 0;
  const closeViewer = () => setViewerIndex(null);
  useCloseOnBack(viewerOpen, closeViewer);

  // Ohne `imageOrientation`: die Brett-Oberfläche samt diesem Viewer zeigt Karten hochkant, nur
  // die schwebende Großansicht zeigt Querseiten quer (Spec 2026-10-31 §6.3).
  const viewerCards = useMemo(() => filtered.map((card) => {
    const { src, backSrc, alt, face } = cardImageProps(card);
    return { imageUrl: src, imageUrlBack: backSrc, name: alt, face };
  }), [filtered]);

  const countLabel = cards.length === 1 ? '1 Karte' : `${cards.length} Karten`;

  return (
    <>
      <SchemaModal
        open={open}
        onOpenChange={onOpenChange}
        size="lg"
        title={title}
        description={countLabel}
        dismissOnBackdrop={!viewerOpen}
      >
        <div className="card-list-viewer">
          {cards.length > 0 && (
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClear={() => setQuery('')}
              placeholder="Nach Name filtern"
              aria-label="Nach Name filtern"
            />
          )}
          {cards.length === 0 ? (
            <EmptyState icon={Layers} title={emptyTitle} />
          ) : filtered.length === 0 ? (
            <EmptyState variant="filter" icon={SearchX} title="Keine Karte gefunden" description="Passe den Namen an." />
          ) : (
            <CardGrid variant={isMobile ? 'touch' : 'browse'}>
              {filtered.map((card, index) => (
                <ViewerCard
                  key={card.instanceId}
                  card={card}
                  zone={zone}
                  allowDrag={allowDrag}
                  onTap={isMobile ? () => setViewerIndex(index) : undefined}
                  onCardAction={onCardAction}
                />
              ))}
            </CardGrid>
          )}
        </div>
        {children}
      </SchemaModal>
      <CardViewer
        open={open && viewerOpen}
        cards={viewerCards}
        index={viewerIndex ?? 0}
        onIndexChange={setViewerIndex}
        onClose={closeViewer}
        label={`${Math.min((viewerIndex ?? 0) + 1, viewerCards.length)} von ${viewerCards.length}`}
        raised
      />
    </>
  );
}

function ViewerCard({ card, zone, allowDrag, onTap, onCardAction }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dragId(card.instanceId),
    data: dragData(card, zone),
    disabled: !allowDrag,
  });
  const { face, ...imageProps } = cardImageProps(card);

  const handleContextMenu = (e) => {
    if (!onCardAction) return;
    e.preventDefault();
    onCardAction(card, { x: e.clientX, y: e.clientY });
  };

  return (
    <div
      ref={setNodeRef}
      className={`card-list-viewer__card${allowDrag ? ' card-list-viewer__card--draggable' : ''}${isDragging ? ' card-list-viewer__card--dragging' : ''}`}
      data-testid="card-list-viewer-card"
      data-instance-id={card.instanceId}
      {...(allowDrag ? attributes : {})}
      {...listeners}
      onClick={onTap}
      onContextMenu={handleContextMenu}
    >
      <CardImage {...imageProps} defaultFace={face} />
    </div>
  );
}
